import { useCallback, useEffect, useRef, useState } from 'react';
import type { OutstandingResponse } from '@gc/shared';

import { api, ApiError } from '@/lib/api';

/**
 * The Outstanding payload, polled.
 *
 * Running sessions cost more every second, so a static figure at the till is a
 * figure that is already wrong. 10s matches the device board's poll — the two
 * screens quote the same money, and staff move between them.
 *
 * A `requestId` guard discards a slow response that lands after a newer one, the
 * same pattern `useBills` uses: without it a reload can be overwritten by the
 * poll it raced.
 */
const POLL_MS = 10_000;

export function useOutstanding(): {
  data: OutstandingResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
} {
  const [data, setData] = useState<OutstandingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const requestId = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    const id = requestId.current + 1;
    requestId.current = id;
    try {
      const response = await api.get<OutstandingResponse>('/tabs/outstanding');
      if (requestId.current !== id) return;
      setData(response);
      setError(null);
    } catch (caught) {
      if (requestId.current !== id) return;
      setError(caught instanceof ApiError ? caught.message : 'Could not load outstanding bills.');
    } finally {
      if (requestId.current === id) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = setInterval(() => void load(), POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  return { data, loading, error, reload: load };
}
